import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingCart, User, Menu, X, LayoutDashboard, Search, BadgePercent } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { fmtPrice } from "../lib/shop";

const NAV_LINKS = [
  { label: "Produse", to: "/produse" },
  { label: "De ce noi", to: "/#de-ce-noi" },
  { label: "Recenzii", to: "/#recenzii" },
  { label: "Pentru magazine", to: "/#pentru-magazine" },
  { label: "Contact", to: "/#contact" },
];

function slugify(s) {
  return s.toLowerCase().replace(/\s+/g, "-").replace(/ă/g, "a").replace(/ș/g, "s");
}

export function Navbar() {
  const { user } = useAuth();
  const { count, setDrawerOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [hasPromo, setHasPromo] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      setSearching(true);
      api.get("/products", { params: { q: searchQ.trim() } })
        .then((r) => setSearchResults(r.data.slice(0, 6)))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  const closeSearch = () => { setSearchOpen(false); setSearchQ(""); };

  const goHome = (e) => {
    if (location.pathname === "/" && !location.hash) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    api.get("/content").then((r) => setAnnouncement(r.data.announcement || "")).catch(() => {});
    api.get("/products", { params: { promo: true } }).then((r) => setHasPromo(r.data.length > 0)).catch(() => {});
  }, []);

  const renderLink = (link, onClick) =>
    link.to.includes("#") ? (
      <Link
        key={link.to}
        to={link.to}
        onClick={onClick}
        className="text-sm font-semibold text-ink transition-colors hover:text-navy"
        data-testid={`nav-link-${slugify(link.label)}`}
      >
        {link.label}
      </Link>
    ) : (
      <NavLink
        key={link.to}
        to={link.to}
        onClick={onClick}
        className={({ isActive }) =>
          `text-sm font-semibold transition-colors hover:text-navy ${isActive ? "text-navy" : "text-ink"}`
        }
        data-testid={`nav-link-${slugify(link.label)}`}
      >
        {link.label}
      </NavLink>
    );

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {announcement && (
        <div className="bg-navy px-4 py-1.5 text-center sm:py-2" data-testid="announcement-bar">
          <p className="text-[11px] font-semibold leading-tight text-white sm:text-xs">{announcement}</p>
        </div>
      )}
      <div className="border-b border-line bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1120px] items-center justify-between gap-4 px-4 sm:h-20 sm:px-8">
          <Link to="/" onClick={goHome} data-testid="nav-logo" aria-label="zilnic — pagina principală">
            <Logo className="h-8 w-auto sm:h-10" />
          </Link>
          <nav className="hidden items-center gap-6 lg:flex" data-testid="nav-links">
            {NAV_LINKS.map((l) => renderLink(l))}
          </nav>
          <div className="flex items-center gap-1.5">
            {hasPromo && (
              <Link
                to="/produse?promo=1"
                className="mr-1 hidden h-11 items-center gap-1.5 rounded-full border-2 border-navy px-5 text-sm font-bold text-navy transition-colors hover:bg-skytint md:flex"
                data-testid="nav-promo-button"
              >
                <BadgePercent className="h-4 w-4" />
                Promoții
              </Link>
            )}
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden h-11 w-11 items-center justify-center rounded-lg text-ink transition-colors hover:bg-skytint hover:text-navy sm:flex"
              aria-label="Caută produse"
              data-testid="nav-search-button"
            >
              <Search className="h-5 w-5" />
            </button>
            {["admin", "operator"].includes(user?.role) && (
              <Link
                to="/admin"
                className="hidden h-11 w-11 items-center justify-center rounded-lg text-ink transition-colors hover:bg-skytint hover:text-navy sm:flex"
                aria-label="Panou admin"
                data-testid="nav-admin-link"
              >
                <LayoutDashboard className="h-5 w-5" />
              </Link>
            )}
            <Link
              to={user ? "/cont" : "/autentificare"}
              className="flex h-11 w-11 items-center justify-center rounded-lg text-ink transition-colors hover:bg-skytint hover:text-navy"
              aria-label="Contul meu"
              data-testid="nav-account-button"
            >
              <User className="h-5 w-5" />
            </Link>
            <button
              onClick={() => setDrawerOpen(true)}
              className="relative flex h-11 w-11 items-center justify-center rounded-lg text-ink transition-colors hover:bg-skytint hover:text-navy"
              aria-label="Coș de cumpărături"
              data-testid="nav-cart-button"
            >
              <ShoppingCart className="h-5 w-5" />
              <AnimatePresence>
                {count > 0 && (
                  <motion.span
                    key={count}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-navy px-1 text-[10px] font-bold text-white"
                    data-testid="cart-count-badge"
                  >
                    {count}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-11 w-11 items-center justify-center rounded-lg text-ink hover:bg-skytint lg:hidden"
              aria-label="Meniu"
              data-testid="mobile-menu-button"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden border-t border-line lg:hidden"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col gap-1 px-3 py-4">
                <button
                  onClick={() => { setMenuOpen(false); setSearchOpen(true); }}
                  className="flex items-center gap-3 rounded-xl px-3 py-3.5 text-left text-base font-bold text-navy transition-colors hover:bg-skytint"
                  data-testid="mobile-nav-search"
                >
                  <Search className="h-5 w-5" /> Caută produse
                </button>
                <Link
                  to="/cont"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 rounded-xl px-3 py-3.5 text-base font-bold text-navy transition-colors hover:bg-skytint"
                  data-testid="mobile-nav-account"
                >
                  <User className="h-5 w-5" /> Contul meu
                </Link>
                {hasPromo && (
                  <Link to="/produse?promo=1" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 rounded-xl px-3 py-3.5 text-base font-bold text-navy transition-colors hover:bg-skytint" data-testid="mobile-nav-promo">
                    <BadgePercent className="h-5 w-5" /> Promoții
                  </Link>
                )}
                <div className="[&_a]:block [&_a]:rounded-xl [&_a]:px-3 [&_a]:py-3.5 [&_a]:!text-base">
                  {NAV_LINKS.map((l) => renderLink(l, () => setMenuOpen(false)))}
                </div>
                {["admin", "operator"].includes(user?.role) && (
                  <Link to="/admin" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 rounded-xl px-3 py-3.5 text-base font-bold text-navy transition-colors hover:bg-skytint" data-testid="mobile-nav-admin-link">
                    <LayoutDashboard className="h-5 w-5" /> Panou Admin
                  </Link>
                )}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>

      {/* Overlay căutare — rezultate live */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-navy/40 p-4 backdrop-blur-sm"
            onClick={closeSearch}
            data-testid="search-overlay"
          >
            <motion.div
              initial={{ y: -16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -16, opacity: 0 }}
              className="mx-auto mt-16 w-full max-w-xl rounded-2xl bg-white p-4 shadow-2xl sm:mt-24"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 border-b border-line pb-3">
                <Search className="h-5 w-5 shrink-0 text-muted2" />
                <input
                  autoFocus
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Escape" && closeSearch()}
                  placeholder="Ce cauți? Ex: detergent, balsam, săpun..."
                  className="w-full min-w-0 flex-1 text-base text-ink outline-none placeholder:text-muted2"
                  data-testid="search-input"
                />
                <button onClick={closeSearch} aria-label="Închide căutarea" className="shrink-0 text-muted2 hover:text-navy" data-testid="search-close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              {searchQ.trim() !== "" && (
                <div className="max-h-[55vh] overflow-y-auto pt-2" data-testid="search-results">
                  {searching && <p className="py-6 text-center text-sm text-muted2">Se caută...</p>}
                  {!searching && searchResults.length === 0 && (
                    <p className="py-6 text-center text-sm text-muted2" data-testid="search-empty">Niciun produs găsit pentru „{searchQ}".</p>
                  )}
                  {searchResults.map((p) => (
                    <Link
                      key={p.id}
                      to={`/produs/${p.id}`}
                      onClick={closeSearch}
                      className="flex items-center gap-3 rounded-xl p-2.5 transition-colors hover:bg-skytint"
                      data-testid={`search-result-${p.id}`}
                    >
                      <img src={p.image} alt="" className="h-12 w-12 shrink-0 rounded-lg border border-line object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-navy">{p.name}</p>
                        <p className="text-xs text-muted2">{p.volume}</p>
                      </div>
                      <span className="shrink-0 text-sm font-bold text-navy">{fmtPrice(p.price)}</span>
                    </Link>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
